/**
 * ScrollToTop Component
 *
 * Floating button that appears when user scrolls down, allowing smooth scroll to top.
 * Story 1-5: ManualPage Layout Integration
 */

import { useState, useEffect, useCallback, useRef } from 'react';

interface ScrollToTopProps {
  /** Scroll threshold in pixels before button appears (default: 300) */
  threshold?: number;
  /** Target element to scroll (default: window) */
  targetRef?: React.RefObject<HTMLElement | null>;
}

/**
 * Throttle function to limit scroll handler calls for performance
 */
function throttle<T extends (...args: unknown[]) => void>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let lastCall = 0;
  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= limit) {
      lastCall = now;
      func(...args);
    }
  };
}

/**
 * ChevronUp icon (inline SVG)
 */
function ChevronUpIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}

/**
 * ScrollToTop - Floating scroll-to-top button
 *
 * Appears when user scrolls past threshold, provides smooth scroll to top.
 * Positioned fixed in bottom-right corner of viewport.
 */
export function ScrollToTop({ threshold = 300, targetRef }: ScrollToTopProps) {
  const [isVisible, setIsVisible] = useState(false);

  // Scroll handler
  const updateVisibility = useCallback(() => {
    const scrollTop = targetRef?.current
      ? targetRef.current.scrollTop
      : window.scrollY;
    setIsVisible(scrollTop > threshold);
  }, [threshold, targetRef]);

  // Create throttled version - throttle to 100ms for performance
  const throttledHandlerRef = useRef<(() => void) | null>(null);
  if (!throttledHandlerRef.current) {
    throttledHandlerRef.current = throttle(() => updateVisibility(), 100);
  }

  useEffect(() => {
    const target = targetRef?.current || window;
    const handler = throttledHandlerRef.current!;

    // Use passive listener for better scroll performance
    target.addEventListener('scroll', handler, { passive: true });
    // Initial check
    updateVisibility();

    return () => {
      target.removeEventListener('scroll', handler);
    };
  }, [updateVisibility, targetRef]);

  const scrollToTop = () => {
    const target = targetRef?.current;
    if (target) {
      target.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (!isVisible) return null;

  return (
    <button
      type="button"
      onClick={scrollToTop}
      className="fixed bottom-6 right-6 z-50 p-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 transform hover:scale-105"
      aria-label="Terug naar boven"
      data-testid="back-to-top"
    >
      <ChevronUpIcon className="w-5 h-5" />
    </button>
  );
}
